import { inject, Injectable } from "@angular/core";
import { WINDOW_TOKEN } from "../browser";
import { APP_CONFIG_TOKEN } from "../config";
import { SessionService } from "../session";

interface IntercomFunction {
    (command: string, ...args: unknown[]): void;
    q: unknown[][];
    c: (args: unknown[]) => void;
}

declare global {
    interface Window {
        Intercom?: IntercomFunction;
        intercomSettings?: Record<string, unknown>;
    }
}

@Injectable({
    providedIn: "root"
})
export class IntercomService {
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly window = inject(WINDOW_TOKEN);
    private readonly sessionService = inject(SessionService);

    private intercomAppId: string | undefined;

    public boot(): void {
        const profile = this.sessionService.profileData();
        if (!profile) {
            return;
        }

        this.intercomAppId = profile.intercomAppId || this.appConfig.intercomAppId;

        if (!this.window.Intercom && this.intercomAppId) {
            this.initializeWidget(this.intercomAppId);
        }

        if (profile.intercomAppId) {
            this.window.Intercom?.("boot", {
                app_id: this.intercomAppId,
                user_id: profile.userId,
                intercom_user_jwt: profile.intercomUserJwt,
                company: {
                    company_id: String(profile.kno2OrganizationId),
                    name: profile.organizationName
                }
            });
        }
    }

    private initializeWidget(appId: string): void {
        if (!appId) {
            console.debug(`IntercomService: No appId provided, service is disabled.`);
            return;
        }

        console.debug(`IntercomService: Initializing Intercom widget with appId:`, appId);

        if (typeof this.window.Intercom === "function") {
            this.window.Intercom("reattach_activator");
            this.window.Intercom("update", this.window.intercomSettings);
        } else {
            const intercomStub = ((...args: unknown[]): void => {
                intercomStub.c(args);
            }) as IntercomFunction;
            intercomStub.q = [];
            intercomStub.c = (args: unknown[]): void => {
                intercomStub.q.push(args);
            };
            this.window.Intercom = intercomStub;

            const loadIntercomScript = () => {
                const scriptElement = this.window.document.createElement("script");
                scriptElement.type = "text/javascript";
                scriptElement.async = true;
                scriptElement.src = `https://widget.intercom.io/widget/${appId}`;
                const firstScript = this.window.document.getElementsByTagName("script")[0];
                firstScript.parentNode?.insertBefore(scriptElement, firstScript);
            };

            if (this.window.document.readyState === "complete") {
                loadIntercomScript();
            } else {
                this.window.addEventListener("load", loadIntercomScript, false);
            }
        }
    }
}
